import { Button as ButtonComponent } from 'crux';
import React, { useEffect } from 'react';
import get from 'lodash/get';
import styled, { withTheme } from 'styled-components';
import { connect } from 'react-redux';
import HELPER from '../redux/reducers/helper';
import {
  getCompiledQuestionsAnswersErrors,
  submitSectionQuestion,
  updateAnswersArray,
} from '../redux/actions/questions';
import { postMakeCopyApp } from '../util/externalServices';

const ButtonWrapper = styled.div`
  display: flex;
  margin: ${({ wrapperMargin }) => wrapperMargin || '10px 0'};
  ${({ align }) =>
    align === 'left' &&
    `
        justify-content: flex-start;
    `};
  ${({ align }) =>
    align === 'center' &&
    `
        justify-content: center;
        align-content: center;
    `};
  ${({ align }) =>
    align === 'right' &&
    `
        justify-content: flex-end;
    `};
  & > button {
    ${({ themeConfiguration: { minWidth } = {}, showArrowBtn }) =>
      !showArrowBtn && minWidth && `min-width: ${minWidth}`};
    ${({ width, showArrowBtn }) => !showArrowBtn && width && `width: ${width}`};
    ${({ padding }) => padding && `padding: ${padding}`};
    @media (max-width: 850px) {
      ${({ themeConfiguration: { mobileWidth, mobileMinWidth } }) =>
        `min-width: ${mobileMinWidth};width:${mobileWidth};`};
    }
    ${({ showArrowBtn }) =>
      showArrowBtn &&
      `
      border: none;
      &:hover {
        border: none;
        background-color: #ffffff;
        color: inherit;
      }
    `};
    &:focus {
      outline: 4px solid #037bb5;
      outline-offset: 1px;
      // For safari browsers because it has issues whith outline when border:none;
      border: 0px solid #037bb5;
    }
  }
  &:focus {
    outline: none;
  }
`;

// Button Width setting
const ButtonWidth = {
  S: 100,
  M: 260,
  L: 360,
  XL: 560,
};

const BUTTON_SELECTORS = {
  GetRateClassBtn: '#AgentIncome',
  RequoteButton: '.react-tabs__tab-list li',
  AppPhaseRequoteButton: '.react-tabs__tab-list li',
  // 'Requote.SendQuoteButton': '#select-Requote.PurchasePUA',
};

// Defining default styling rules for Button
const defaultTheme = {
  type: 'outlined',
  align: 'center',
  width: `${ButtonWidth.L}px`,
};

const getButtonTheme = (question, pageOverrides, pageId) => {
  if (question && question.styles) return question.styles;
  if (question && pageOverrides && pageOverrides[pageId]) {
    const pageButtons = get(pageOverrides[pageId], 'buttons', {});
    return get(pageButtons[question.question_id], 'btnTheme', defaultTheme);
  }
  return defaultTheme;
};

const showArrowBtn = (csrConfig = {}, isEnabled, breadcrumbId = '') => {
  const isQuote = window.location.pathname === '/quote';
  const isRequoteScreen = breadcrumbId?.toLowerCase() === 'applicationrequotebreadcrumb';
  const enableBtn = Object.keys(csrConfig).some((section) =>
    get(csrConfig, [[section], 'enable'], false),
  );
  return !isQuote && isRequoteScreen && (enableBtn || !isEnabled);
};

const getRequoteBtnsTheme = (csrConfig = {}, arrowTheme = {}, isEnabled, breadcrumbId = '') => {
  return showArrowBtn(csrConfig, isEnabled, breadcrumbId) ? arrowTheme : {};
};

const Button = (props) => {
  const {
    question,
    pageId,
    isLoading,
    enableBtn,
    requiredQuestions = [],
    csrConfigObj,
    areAnswersValid,
    questionList = [],
    handleActionBtn,
    handleCsrConfig = () => {},
    theme,
    lastFocusedElementId,
    popupQuestions,
    handleModalBtnClick,
    isCalculateBtn,
    // role,
    screenViewport,
  } = props;
  const { question_id: questionId, question_text: questionText, properties } = question;

  const [isProcessing, setIsProcessing] = React.useState(false);
  const csrKey = get(question, 'properties.csr.value');

  useEffect(() => {
    if (isProcessing) {
      if (!isLoading && !isCalculateBtn) setIsProcessing(false);
    }
  }, [isLoading, isProcessing]);

  useEffect(() => {
    if (lastFocusedElementId) {
      setTimeout(() => {
        // Placed in timeout, as plans grid answer needs to be whole life to show email illustration button
        // And focus should be preserved once it is visible
        const buttonEl = document.getElementById(lastFocusedElementId);
        if (buttonEl && buttonEl?.tagName === 'BUTTON' && !buttonEl?.disabled) {
          buttonEl.focus();
        } else {
          // focus unanswered question
          // or focus next button in the flow
          const selector = BUTTON_SELECTORS[lastFocusedElementId];
          if ((!buttonEl || buttonEl?.disabled) && selector) {
            const nextEl = document.querySelector(selector);
            if (nextEl && typeof nextEl.focus === 'function') {
              nextEl.focus();
            }
          }
        }
      }, 100);
    }
    if (csrKey) {
      const csrEnabled = get(question, 'properties.enabled.value');
      let revisedCsrConfig = null;
      if (csrEnabled) {
        if (csrKey === 'rateClass') {
          revisedCsrConfig = {
            rateClass: {
              enable: areAnswersValid,
              show: false,
            },
            quoteResults: {
              enable: false,
              show: false,
            },
            quoteSummary: {
              enable: false,
              show: false,
            },
          };
        } else if (csrKey === 'quoteResults') {
          const rateClassEnable = get(
            questionList.filter((q) => get(q, 'properties.csr.value') === 'rateClass')[0],
            'properties.enabled.value',
          );
          revisedCsrConfig = {
            quoteResults: {
              enable: rateClassEnable ? false : areAnswersValid,
              show: false,
            },
            quoteSummary: {
              enable: false,
              show: false,
            },
          };
        } else if (csrKey === 'quoteSummary') {
          const requoteQs = questionList.filter((q) => get(q, 'properties.group.value'));
          const quoteResultsQs = requoteQs.filter(
            (q) => get(q, 'properties.csr.value') === 'quoteResults',
          );
          const isQuoteResultsEnabled = get(quoteResultsQs, `${[0]}.properties.enabled.value`);
          revisedCsrConfig = {
            quoteSummary: {
              enable: isQuoteResultsEnabled ? false : areAnswersValid,
              show: false,
            },
          };
        }
      } else if (csrKey === 'rateClass') {
        revisedCsrConfig = {
          rateClass: {
            enable: false,
            show: true,
          },
        };
      } else if (csrKey === 'quoteResults') {
        const rateClassEnable = get(
          questionList.filter((q) => get(q, 'properties.csr.value') === 'rateClass')[0],
          'properties.enabled.value',
        );
        revisedCsrConfig = {
          quoteResults: {
            enable: rateClassEnable ? false : csrEnabled,
            show: !rateClassEnable,
          },
        };
      } else if (csrKey === 'quoteSummary') {
        const isRateClassEnabled = get(
          questionList.filter((q) => get(q, 'properties.csr.value') === 'rateClass')[0],
          'properties.enabled.value',
        );
        const requoteQs = questionList.filter((q) => get(q, 'properties.group.value'));
        const quoteResultsQs = requoteQs.filter(
          (q) => get(q, 'properties.csr.value') === 'quoteResults',
        );
        const isQuoteResultsEnabled = get(quoteResultsQs, `${[0]}.properties.enabled.value`);
        revisedCsrConfig = {
          quoteSummary: {
            enable: isRateClassEnabled || isQuoteResultsEnabled ? false : csrEnabled,
            show: !(isRateClassEnabled || isQuoteResultsEnabled),
          },
        };
      }
      handleCsrConfig(revisedCsrConfig);
    }
  }, []);

  const {
    config: {
      theme: { global, pageOverrides },
    },
  } = theme;

  const buttonTheme = getButtonTheme(question, pageOverrides, pageId);

  const commonBtnTheme = get(global, 'commonButtonTheme');
  const colorOverride = get(global, 'colorOverride', null);
  const modalButtonTheme = get(global, 'modalButton.themeConfiguration', {});
  const arrowBtnTheme = get(global, 'nextButtonTheme.themeConfiguration', {});

  let themeConfiguration = colorOverride
    ? {
        selectedBgColor: colorOverride.selectedBgColor,
        bgColor: '#ffffff',
        border: `1px solid ${colorOverride.selectedBgColor}`,
        fontColor: '#333333',
      }
    : { ...modalButtonTheme, ...commonBtnTheme };

  // To override styles from BE for mobile sizes in quote page
  if (pageId === 'quote' && screenViewport === 'mobile') {
    buttonTheme.width = '90%';
  }
  themeConfiguration = themeConfiguration && {
    ...themeConfiguration,
    ...commonBtnTheme,
    ...getRequoteBtnsTheme(csrConfigObj, arrowBtnTheme, enableBtn, pageId),
  };

  const dataActions = get(properties, 'data-action', { type: 'NONE' });
  const modalRefId = get(properties, 'modal_reference_id', null);

  const onClickHandler = async () => {
    // Support for data-action types (from response)
    switch (dataActions.type) {
      case 'REDIRECT':
        window.location.href = dataActions.link || '';
        break;
      case 'REFRESH':
        window.location.reload();
        break;
      case 'API': {
        setIsProcessing(true);
        handleActionBtn(requiredQuestions);
        break;
      }
      case 'COPY_APP': {
        await postMakeCopyApp({ payload: dataActions.body });
        break;
      }
      case 'EXIT':
        window.close();
        break;
      case 'NONE':
      default:
        break;
    }
    if (modalRefId) {
      handleModalBtnClick(popupQuestions);
    }
  };

  // Todo: Make this dynamic using a prop from BE
  if (!csrKey && questionId === 'AgentEmailIllustration') {
    const plansGridQ = requiredQuestions.find((q) => q.question_id === 'AgentPlansGrid');
    if (plansGridQ?.answer?.category !== 'Whole Life') {
      return null;
    }
  }

  if (
    csrKey === 'quoteResults' &&
    (get(csrConfigObj, 'quoteResults.show') || !get(csrConfigObj, 'rateClass.show'))
  ) {
    return null;
  }

  if (
    csrKey === 'quoteSummary' &&
    (get(csrConfigObj, 'quoteResults.enable') ||
      !enableBtn ||
      get(csrConfigObj, 'quoteSummary.show'))
  ) {
    return null;
  }

  // const btnAttrs = pageId === 'quote' && role && { tabIndex: '0' };
  const labelText = isProcessing ? 'PROCESSING...' : questionText;

  return (
    <ButtonWrapper
      { ...buttonTheme }
      // {...btnAttrs}
      showArrowBtn={ showArrowBtn(csrConfigObj, enableBtn, pageId) }
    >
      <ButtonComponent
        label={ labelText }
        shape={ get(commonBtnTheme, 'shape') }
        handleButtonClick={ onClickHandler }
        themeConfiguration={ themeConfiguration }
        disabled={ isProcessing || !enableBtn }
        id={ questionId }
      />
    </ButtonWrapper>
  );
};

const mapStateToProps = (state, props) => {
  const sections = get(state, 'questions.sections');
  const screenViewport = get(state, 'meta.screenViewport');
  const activeSubsectionId = get(state, 'questions.activeSubsectionId', '');

  const requiredSchemaList = get(props, 'question.properties.required_questions_list', []);
  const csrKey = get(props, 'question.properties.csr.value');
  const role = get(state, 'questions.role', '');
  const questionId = get(props, 'question.question_id');
  const isCalculateBtn = questionId === 'Quote.NeedsCalcCalulateButton';

  // For Individual button - In quote screen with custom role
  if (csrKey) {
    const csrGroup = get(props, 'question.properties.group.value');
    const { csrConfig, requoteCsrConfig } = get(state, 'questions');
    const csrConfigObj = csrGroup === 'requote' ? requoteCsrConfig : csrConfig;
    const {
      questionList = [],
      answerList = [],
      errorList,
    } = getCompiledQuestionsAnswersErrors(state);

    const UNEDITABLE_FIELDS = ['group', 'question_group', 'label', 'button'];

    const submitQuestions = ['Requote.backdateagetext'];

    // requested visible questions
    const actualVisibleQs = requiredSchemaList
      .map((id) => questionList.find((q) => q.question_id === id))
      .filter(
        (q) =>
          q !== undefined &&
          !q.is_hidden &&
          (!UNEDITABLE_FIELDS.includes(q.question_type) || submitQuestions.includes(q.question_id)),
      );
    // requested visible answers
    const actualVisibleAnswers = actualVisibleQs.map((question) =>
      answerList.find((q) => q.question_id === question.question_id),
    );

    // required visible requested questions
    const actualRequiredVisibleQs = actualVisibleQs.filter((question) =>
      get(question, 'validations.required.value', false),
    );
    // required visible requested answers
    const actualRequiredVisibleAnswers = actualRequiredVisibleQs.map((question) =>
      answerList.find((q) => q.question_id === question.question_id),
    );

    let requiredQError = errorList?.some((eQ) =>
      actualRequiredVisibleQs.find((q) => q.question_id === eQ.question_id),
    );
    if (errorList?.length === 0) {
      requiredQError = actualRequiredVisibleQs.some((q) => get(q, 'question_status') === 'invalid');
    }

    const areAnswersValid =
      !requiredQError &&
      actualRequiredVisibleAnswers.every((ans) => ans.answer || ans.answer === 0);

    const lastFocusedElementId = get(state, 'questions.lastFocusedElementId', '');

    return {
      enableBtn: csrConfigObj[csrKey].enable,
      pageId: activeSubsectionId,
      requiredQuestions: actualVisibleAnswers,
      csrConfigObj,
      questionList,
      areAnswersValid,
      screenViewport,
      lastFocusedElementId,
      isCalculateBtn,
      isLoading: get(state, 'questions.isLoading', false),
    };
  }

  // For Individual button, if we get required_questions_list in BE response
  if (requiredSchemaList.length) {
    const { questionList, answerList, errorList } = getCompiledQuestionsAnswersErrors(state);
    const revisedErrorsArr = errorList.filter((err) =>
      requiredSchemaList.includes(err.question_id),
    );
    const revisedRequiredSchema = questionList.filter((q) =>
      requiredSchemaList.includes(q.question_id),
    );
    const requiredSchema = HELPER.getRequiredSchema(revisedRequiredSchema);

    const enableBtn =
      requiredSchemaList.length === revisedRequiredSchema.length &&
      HELPER.shouldEnableContinue(requiredSchema, answerList, revisedErrorsArr);

    let actualRequiredAnswers = answerList.filter((ans) =>
      requiredSchemaList.includes(ans.question_id),
    );
    actualRequiredAnswers = actualRequiredAnswers.map((obj) => ({
      ...obj,
      answer:
        typeof obj.answer === 'object' && get(obj, 'answer.id')
          ? get(obj, 'answer.id')
          : obj.answer,
    }));

    const lastFocusedElementId = get(state, 'questions.lastFocusedElementId', '');
    return {
      enableBtn,
      pageId: activeSubsectionId,
      requiredQuestions: actualRequiredAnswers,
      questionList,
      lastFocusedElementId,
      role,
      isCalculateBtn,
      screenViewport,
      isLoading: get(state, 'questions.isLoading', false),
    };
  }

  // For individual button part of any other questions where its child questions are skipped from being flattened
  // eg: Accordion (question.properties.appearance.value is accordion)
  const requiredSchema = HELPER.getRequiredSchema(get(props, 'parentQuestion.questions', []));
  const { answersArray = [], errorsArray = [] } = sections[activeSubsectionId]
    ? sections[activeSubsectionId][0]
    : {};
  const revisedErrorsArr = errorsArray.filter((err) => requiredSchema.includes(err.question_id));
  const enableBtn = HELPER.shouldEnableContinue(requiredSchema, answersArray, revisedErrorsArr);
  const popupQuestions = get(state, 'questions.popupQuestions', []);
  return {
    enableBtn,
    pageId: activeSubsectionId,
    popupQuestions,
    role,
    screenViewport,
    isCalculateBtn,
    isLoading: get(state, 'questions.isLoading', false),
  };
};

const mapDispatchToProps = (dispatch, props) => {
  const { sectionIndex, question } = props;
  return {
    handleActionBtn: (requiredQuestions = []) => {
      const questionId = get(props, 'question.question_id');
      const isCalculateBtn = questionId === 'Quote.NeedsCalcCalulateButton';
      const isReflexive = isCalculateBtn || get(props, 'question.reflexive_question_flag');
      dispatch(
        submitSectionQuestion(sectionIndex, {
          isReflexive,
          sectionIndex,
          preventReflexiveCall: true,
          calculateClick: isCalculateBtn,
          requiredQuestions,
          onClick: questionId,
        }),
      );
    },
    handleCsrConfig: (defaultCsrConfig) => {
      dispatch(
        updateAnswersArray({
          questionId: question.question_id,
          response: question.response,
          sectionIndex: props.sectionIndex,
          propQuestion: props.question,
          defaultCsrConfig,
        }),
      );
    },
  };
};

export default withTheme(connect(mapStateToProps, mapDispatchToProps)(Button));
